'use client';
import type { ColorScheme, InterfaceEntity } from '@unifyapps/defs/types/interface';
import { Stack } from '@unifyapps/ui/components/Stack';
import { ThemeOverrideContextProvider } from '@unifyapps/ui/providers/ThemeOverrideContext';
import { useInterfaceResourceStrategy } from '../../context/InterfaceResourceStrategyProvider';
import type BlocksRegistry from '../../context/RegistryProvider/BlocksRegistry';
import { useDataColorScheme } from '../../hooks/useDataColorScheme';
import { useGetDeviceDetailsInStateStore } from '../../stores/app/GlobalAppStateStore';
import { InterfaceClient, InterfaceModes } from '../../stores/InterfaceStore';
import type { InterfaceComponentPage } from '../../types/interfaceComponent';
import { getDimensionClasses } from '../../utils/getDimensionClasses';
import { InterfaceAppResourceLoader } from '../app/InterfaceAppResourceLoader';
import { APP_RESOURCES_STRATEGY_VS_HOOK } from '../app/InterfaceAppResourceLoader/constants/resourceStrategyVsHook';
import { InterfacePage } from '../page/InterfacePage';
import { InterfacePageResourceLoader } from '../page/InterfacePageResourceLoader';
import { PAGE_RESOURCES_STRATEGY_VS_HOOK } from '../page/InterfacePageResourceLoader/constants/resourceStrategyVsHook';

const matrixClient = InterfaceClient.UA_MATRIX;
const matrixMode = InterfaceModes.RUNNER;

type PublicPageProps = {
  interfaceComponent: InterfaceComponentPage;
  interfaceRecord: InterfaceEntity;
  searchParams?: {
    returnTo?: string;
  };
  registry: BlocksRegistry;
  colorScheme?: ColorScheme;
  interfaceThemeId?: string;
  pageParams?: Record<string, string | undefined>;
};

function InterfacePageLayout() {
  const { device } = useGetDeviceDetailsInStateStore();
  const dimensionClasses = getDimensionClasses(device);
  const colorScheme = useDataColorScheme();

  return (
    <Stack className={dimensionClasses} data-color-scheme={colorScheme} data-page-type="governance">
      <InterfacePage />
    </Stack>
  );
}

// Public pages in private apps use Authorization Strategy exactly same as Governance pages.
// So renamed GovernancePage to more generic PublicPage.
function PublicPage(props: React.PropsWithChildren<PublicPageProps>) {
  const {
    interfaceRecord,
    interfaceComponent,
    searchParams,
    registry,
    colorScheme,
    interfaceThemeId,
    children,
    pageParams,
  } = props;
  const { strategy } = useInterfaceResourceStrategy();
  const useAppResourceStrategy = APP_RESOURCES_STRATEGY_VS_HOOK[strategy];
  const usePageResourceStrategy = PAGE_RESOURCES_STRATEGY_VS_HOOK[strategy];

  const appResponse = useAppResourceStrategy({
    interfaceId: interfaceRecord.id,
    client: matrixClient,
    mode: matrixMode,
  });

  const pageResponse = usePageResourceStrategy({
    interfaceId: interfaceRecord.id,
    interfaceComponent,
    client: matrixClient,
    mode: matrixMode,
  });

  return (
    <InterfaceAppResourceLoader
      client={matrixClient}
      colorScheme={colorScheme}
      interfaceThemeId={interfaceThemeId}
      mode={matrixMode}
      registry={registry}
      response={appResponse}
    >
      <InterfacePageResourceLoader
        mode={matrixMode}
        pageInputs={searchParams}
        pageParams={pageParams}
        response={pageResponse}
      >
        {/*only in matrix-spa we do not want h-screen etc so we are overriding page layout using child*/}
        {children ? children : <InterfacePageLayout />}
      </InterfacePageResourceLoader>
    </InterfaceAppResourceLoader>
  );
}

function ThemedPublicPage(props: React.PropsWithChildren<PublicPageProps>) {
  const { interfaceRecord } = props;
  const style = interfaceRecord.properties.theme?.output;

  return (
    <ThemeOverrideContextProvider style={style}>
      <PublicPage {...props} />
    </ThemeOverrideContextProvider>
  );
}

export { PublicPage, ThemedPublicPage };
